import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default class ModuleReveal {
    constructor() {
        if (typeof window === 'undefined') return;

        // Respect reduced motion preferences
        const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        this.animateModulesOnScroll();
    }

    // Removed image gating

    animateModulesOnScroll() {
        const allRevealElements = Array.from(document.querySelectorAll('.js-reveal'));
        if (!allRevealElements.length) return;

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
        const startThreshold = isMobile ? 0.99 : 0.85;
        const elementsAboveViewport = [];
        const elementsToAnimate = [];

        // Split elements: those fully above viewport should be instantly visible; others get animated
        allRevealElements.forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.bottom <= 0) {
                elementsAboveViewport.push(el);
            } else {
                elementsToAnimate.push(el);
            }
        });

        // Instantly reveal elements fully above the viewport on load
        if (elementsAboveViewport.length) {
            gsap.set(elementsAboveViewport, { autoAlpha: 1, y: 0 });
            elementsAboveViewport.forEach((el) => { el.dataset.revealed = 'true'; });
        }

        // Prepare elements that should animate on enter
        if (elementsToAnimate.length) {
            gsap.set(elementsToAnimate, { autoAlpha: 0, y: 42 });
        }

        // Immediately reveal elements already within the viewport on load (e.g., cards near the fold)
        const initiallyInView = elementsToAnimate.filter((el) => {
            const rect = el.getBoundingClientRect();
            return rect.top <= viewportHeight * startThreshold && rect.bottom >= 0;
        });
        if (initiallyInView.length) {
            const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
            tl.to(initiallyInView, {
                autoAlpha: 1,
                duration: 0.5,
                ease: 'none',
                stagger: 0.12,
            }, 0);
            tl.to(initiallyInView, {
                y: 0,
                duration: 1,
                ease: 'power3.out',
                stagger: 0.12,
            }, 0);

            // Mark as revealed so ScrollTrigger won't re-animate them
            initiallyInView.forEach((el) => { el.dataset.revealed = 'true'; });
            // Refresh ScrollTrigger to account for elements just revealed
            ScrollTrigger.refresh();
        }

        // Batch elements that enter at the same time and animate with a stagger
        // Start earlier on mobile so items appear sooner
        const startPosition = `top ${Math.round(startThreshold * 100)}%`;

        ScrollTrigger.batch('.js-reveal', {
            start: startPosition,
            once: false,
            onEnter: (batchElements, batchTriggers) => {
                // Filter out elements already revealed (e.g., those above viewport on load)
                const targets = batchElements.filter((el) => !el.dataset.revealed);
                if (!targets.length) return;

                // Sort by vertical position so nearest items animate first
                const sortedTargets = targets.slice().sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

                const tl = gsap.timeline({
                    defaults: { overwrite: 'auto' },
                    onComplete: () => batchTriggers.forEach((t) => t.kill()),
                });

                // Opacity with no easing
                tl.to(sortedTargets, {
                    autoAlpha: 1,
                    duration: 1,
                    ease: 'none',
                    stagger: 0.12,
                }, 0);

                // Transform with natural easing
                tl.to(sortedTargets, {
                    y: 0,
                    duration: 2,
                    ease: 'power3.out',
                    stagger: 0.12,
                }, 0);

                // Mark as revealed to avoid future processing
                sortedTargets.forEach((el) => { el.dataset.revealed = 'true'; });
            },
        });
    }
}


